/**
 * ContextPanel - Persistent message context panel with Telegram-like bubbles
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, ArrowForward as JumpIcon } from '@mui/icons-material';
import { getMessageWindow } from '@/api/messages';
import MessageBubble from './MessageBubble';
import { useMessageBuffer } from '@/hooks/useMessageBuffer';
import { EntrySuggestion } from '@/types/api';

const PAGE_SIZE = 50;
const SCROLL_THRESHOLD = 120;

interface ContextPanelProps {
  open: boolean;
  chatId: number | null;
  chatName?: string;
  anchorId: number | null;
  suggestions?: EntrySuggestion[];
  onClose: () => void;
  onJumpToId: (messageId: number) => void;
}

const ContextPanel: React.FC<ContextPanelProps> = ({
  open,
  chatId,
  chatName,
  anchorId,
  suggestions = [],
  onClose,
  onJumpToId,
}) => {
  const [jumpValue, setJumpValue] = useState('');
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingBefore, setIsLoadingBefore] = useState(false);
  const [isLoadingAfter, setIsLoadingAfter] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const { messages, minId, maxId, hasMoreBefore, hasMoreAfter, reset, prepend, append } =
    useMessageBuffer(240);

  // Build a map of ALL message IDs to their types from ALL suggestions
  const messageTypeMap = useMemo(() => {
    const map = new Map<number, 'TITLE' | 'SEPARATOR' | 'MEDIA'>();

    suggestions.forEach(suggestion => {
      // Add title message
      map.set(suggestion.title_message_id, 'TITLE');

      // Add separator message (if exists)
      if (suggestion.separator_message_id) {
        map.set(suggestion.separator_message_id, 'SEPARATOR');
      }

      // Add all media messages
      suggestion.media_message_ids.forEach(mediaId => {
        map.set(mediaId, 'MEDIA');
      });
    });

    return map;
  }, [suggestions]);

  // Helper function to get message type from map
  const getMessageType = (msgId: number): 'TITLE' | 'SEPARATOR' | 'MEDIA' | null => {
    return messageTypeMap.get(msgId) ?? null;
  };

  // Track the last loaded chat to detect chat changes
  const lastLoadedChatRef = useRef<number | null>(null);
  // Store buffer bounds in refs for checking without causing re-renders
  const bufferBoundsRef = useRef<{ minId: number | null; maxId: number | null }>({ minId: null, maxId: null });

  // Update buffer bounds ref when they change
  useEffect(() => {
    bufferBoundsRef.current = { minId, maxId };
  }, [minId, maxId]);

  // Scroll to target message
  const scrollToAnchor = useCallback((targetId: number) => {
    // Use double requestAnimationFrame for reliable DOM timing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetElement = document.getElementById(`message-${targetId}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }, []);

  // Handle anchor changes - scroll if in buffer, otherwise reload
  useEffect(() => {
    if (!open || !anchorId || !chatId) return;

    const { minId: bufferMin, maxId: bufferMax } = bufferBoundsRef.current;

    // Check if target message is already in buffer (same chat)
    const isMessageInBuffer = bufferMin !== null && bufferMax !== null &&
                               anchorId >= bufferMin && anchorId <= bufferMax &&
                               lastLoadedChatRef.current === chatId;

    if (isMessageInBuffer) {
      // Message already loaded, just scroll to it
      scrollToAnchor(anchorId);
      return;
    }

    // Need to load new messages
    const loadMessages = async () => {
      setIsLoadingInitial(true);
      setError(null);

      try {
        const [before, after] = await Promise.all([
          getMessageWindow({
            chat_id: chatId,
            anchor_id: anchorId,
            direction: 'before',
            limit: PAGE_SIZE,
            include_anchor: true,
          }),
          getMessageWindow({
            chat_id: chatId,
            anchor_id: anchorId,
            direction: 'after',
            limit: PAGE_SIZE,
            include_anchor: false,
          }),
        ]);

        reset([...before.messages, ...after.messages], {
          hasMoreBefore: before.has_more,
          hasMoreAfter: after.has_more,
        });

        lastLoadedChatRef.current = chatId;

        // Scroll after state update
        scrollToAnchor(anchorId);
      } catch (err) {
        setError('Failed to load messages');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadMessages();
  }, [open, anchorId, chatId, reset, scrollToAnchor]);

  const loadBefore = useCallback(async () => {
    if (!chatId || !minId || isLoadingBefore || !hasMoreBefore) return;

    setIsLoadingBefore(true);
    const container = scrollRef.current;
    const previousScrollHeight = container?.scrollHeight ?? 0;
    const previousScrollTop = container?.scrollTop ?? 0;

    try {
      const response = await getMessageWindow({
        chat_id: chatId,
        anchor_id: minId,
        direction: 'before',
        limit: PAGE_SIZE,
        include_anchor: false,
      });

      prepend(response.messages, response.has_more);

      setTimeout(() => {
        if (!container) return;
        const nextScrollHeight = container.scrollHeight;
        container.scrollTop = nextScrollHeight - previousScrollHeight + previousScrollTop;
      }, 0);
    } catch (err) {
      setError('Failed to load earlier messages');
    } finally {
      setIsLoadingBefore(false);
    }
  }, [chatId, minId, isLoadingBefore, hasMoreBefore, prepend]);

  const loadAfter = useCallback(async () => {
    if (!chatId || !maxId || isLoadingAfter || !hasMoreAfter) return;

    setIsLoadingAfter(true);
    try {
      const response = await getMessageWindow({
        chat_id: chatId,
        anchor_id: maxId,
        direction: 'after',
        limit: PAGE_SIZE,
        include_anchor: false,
      });
      append(response.messages, response.has_more);
    } catch (err) {
      setError('Failed to load newer messages');
    } finally {
      setIsLoadingAfter(false);
    }
  }, [chatId, maxId, isLoadingAfter, hasMoreAfter, append]);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop < SCROLL_THRESHOLD && hasMoreBefore && !isLoadingBefore) {
      loadBefore();
    }

    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD && hasMoreAfter && !isLoadingAfter) {
      loadAfter();
    }
  };

  const handleJump = () => {
    const target = Number(jumpValue);
    if (!Number.isNaN(target) && target > 0) {
      onJumpToId(target);
      setJumpValue('');
    }
  };

  if (!open) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        width: 420,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
              {chatName || 'Context'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {anchorId ? `Anchor #${anchorId}` : 'No message selected'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Jump to ID"
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleJump();
            }}
            fullWidth
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleJump}
            disabled={!jumpValue}
            startIcon={<JumpIcon fontSize="small" />}
          >
            Go
          </Button>
        </Box>
      </Box>

      <Divider />

      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
          },
        }}
      >
        {isLoadingInitial && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoadingInitial && !anchorId && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Select a message to view its context
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {!isLoadingInitial && messages.length > 0 && (
          <>
            {isLoadingBefore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}

            {messages.map((message) => {
              const messageType = getMessageType(message.message_id);
              return (
                <MessageBubble
                  key={`${message.message_id}-${message.id}`}
                  message={message}
                  isTarget={anchorId === message.message_id}
                  messageType={messageType}
                />
              );
            })}

            {isLoadingAfter && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ContextPanel;
